/**
 * Grid state: collision, locking, line detection and removal.
 * Pure data + queries. No timing, no rendering, no input.
 */

import type { PieceType, Vec } from './piece.ts';

export const BOARD_WIDTH = 10;
/** Rows the player can see. */
export const VISIBLE_HEIGHT = 20;
/** Hidden rows above the visible field, used for spawning and overflow. */
export const BUFFER_HEIGHT = 20;
export const BOARD_HEIGHT = VISIBLE_HEIGHT + BUFFER_HEIGHT;

/** `null` = empty; otherwise the type of the piece that filled the cell. */
export type Cell = PieceType | null;

export class Board {
  readonly width: number;
  readonly height: number;
  /** Row-major, length = width * height. */
  private cells: Cell[];

  constructor(width = BOARD_WIDTH, height = BOARD_HEIGHT) {
    this.width = width;
    this.height = height;
    this.cells = new Array<Cell>(width * height).fill(null);
  }

  clear(): void {
    this.cells.fill(null);
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  get(x: number, y: number): Cell {
    if (!this.inBounds(x, y)) return null;
    return this.cells[y * this.width + x];
  }

  set(x: number, y: number, value: Cell): void {
    if (!this.inBounds(x, y)) return;
    this.cells[y * this.width + x] = value;
  }

  /**
   * Is this coordinate blocked for a piece?
   * Walls and the floor block. Space above the board does not — pieces spawn
   * and rotate up there.
   */
  isSolid(x: number, y: number): boolean {
    if (x < 0 || x >= this.width) return true;
    if (y >= this.height) return true;
    if (y < 0) return false;
    return this.cells[y * this.width + x] !== null;
  }

  /** True if any of the given cells overlaps a wall, the floor, or a block. */
  collides(cells: readonly Vec[]): boolean {
    for (const c of cells) {
      if (this.isSolid(c.x, c.y)) return true;
    }
    return false;
  }

  /** Stamp cells into the grid. */
  lock(cells: readonly Vec[], type: PieceType): void {
    for (const c of cells) this.set(c.x, c.y, type);
  }

  isRowFull(y: number): boolean {
    if (y < 0 || y >= this.height) return false;
    const base = y * this.width;
    for (let x = 0; x < this.width; x++) {
      if (this.cells[base + x] === null) return false;
    }
    return true;
  }

  isRowEmpty(y: number): boolean {
    if (y < 0 || y >= this.height) return true;
    const base = y * this.width;
    for (let x = 0; x < this.width; x++) {
      if (this.cells[base + x] !== null) return false;
    }
    return true;
  }

  /** Indices of every complete row, top to bottom. */
  findFullRows(): number[] {
    const rows: number[] = [];
    for (let y = 0; y < this.height; y++) {
      if (this.isRowFull(y)) rows.push(y);
    }
    return rows;
  }

  /**
   * Remove the given rows and let everything above fall by the number of rows
   * removed beneath it. Rows may be given in any order; duplicates are ignored.
   */
  clearRows(rows: readonly number[]): void {
    const doomed = new Set(rows.filter((y) => y >= 0 && y < this.height));
    if (doomed.size === 0) return;

    const next = new Array<Cell>(this.width * this.height).fill(null);
    // Walk bottom-up, copying survivors into the lowest free row.
    let writeY = this.height - 1;
    for (let readY = this.height - 1; readY >= 0; readY--) {
      if (doomed.has(readY)) continue;
      const from = readY * this.width;
      const to = writeY * this.width;
      for (let x = 0; x < this.width; x++) next[to + x] = this.cells[from + x];
      writeY--;
    }
    this.cells = next;
  }

  /** Lowest y a piece's cells can be shifted to without colliding. */
  dropDistance(cells: readonly Vec[]): number {
    let distance = 0;
    for (;;) {
      const next = cells.map((c) => ({ x: c.x, y: c.y + distance + 1 }));
      if (this.collides(next)) return distance;
      distance++;
    }
  }

  /** Highest occupied row index, or `height` if the board is empty. */
  highestOccupiedRow(): number {
    for (let y = 0; y < this.height; y++) {
      if (!this.isRowEmpty(y)) return y;
    }
    return this.height;
  }

  /** Flat copy of the grid, for renderers and tests. */
  snapshot(): readonly Cell[] {
    return this.cells;
  }

  clone(): Board {
    const copy = new Board(this.width, this.height);
    copy.cells = [...this.cells];
    return copy;
  }

  /**
   * Build a board from ASCII rows for tests: '.' empty, any other character is
   * treated as that piece type. Rows are bottom-aligned to the board floor.
   */
  static fromStrings(rows: string[], width = BOARD_WIDTH, height = BOARD_HEIGHT): Board {
    const board = new Board(width, height);
    const offset = height - rows.length;
    rows.forEach((row, i) => {
      for (let x = 0; x < row.length; x++) {
        const ch = row[x];
        if (ch !== '.') board.set(x, offset + i, ch as PieceType);
      }
    });
    return board;
  }

  /** Inverse of `fromStrings`, limited to a row range. Test/debug helper. */
  toStrings(fromY = 0, toY = this.height - 1): string[] {
    const out: string[] = [];
    for (let y = fromY; y <= toY; y++) {
      let line = '';
      for (let x = 0; x < this.width; x++) line += this.get(x, y) ?? '.';
      out.push(line);
    }
    return out;
  }
}
